import { reactive, ref } from 'vue';
import { fetchWrapper } from '../utils/FetchWrapper';

export interface PaginationConfig {
    apiEndpoint: string;
    buildApiParams: (page: number) => any;
    initialPageSize?: number;
    pageSizeOptions?: number[];
}

export interface PaginationState {
    currentPage: number;
    pageSize: number;
    pageSizeOptions: number[];
    hasNext: boolean;
    isLoading: boolean;
    loadedPages: number[];
    isInitialLoad: boolean;
}

export function usePagination<T = any>(config: PaginationConfig) {
    const {
        apiEndpoint,
        buildApiParams,
        initialPageSize = 10,
        pageSizeOptions = [10, 20, 50, 100, 500, 1000]
    } = config;

    // Reactive state
    const pagination = reactive<PaginationState>({
        currentPage: 1,
        pageSize: initialPageSize,
        pageSizeOptions,
        hasNext: false,
        isLoading: false,
        loadedPages: [],
        isInitialLoad: true
    });

    const loadedPagesData = new Map<number, T[]>();
    const currentPageData = ref<T[]>([]);
    const errorMessage = ref<string>('');

    // API call function
    const getApiData = async (params: any): Promise<{ data: T[]; pagination: { hasNext: boolean } } | null> => {
        try {
            const queryParams = new URLSearchParams();

            Object.keys(params).forEach((key) => {
                if (params[key] !== undefined && params[key] !== null) {
                    queryParams.append(key, params[key].toString());
                }
            });

            const requestUrl = `${apiEndpoint}?${queryParams.toString()}`;
            const response = await fetchWrapper.get(requestUrl, '');

            if (response.success) {
                // Handle different API response formats
                if (response.result && Array.isArray(response.result)) {
                    // Legacy APIs that return direct array (Payment, Refund, etc.)
                    return {
                        data: response.result,
                        pagination: { hasNext: false } // No pagination for legacy APIs
                    };
                } else if (response.result && response.result.data) {
                    // New APIs with pagination structure
                    return response.result;
                }
                return { data: [], pagination: { hasNext: false } };
            }

            errorMessage.value = response.errors || 'Failed to fetch data';
            return null;
        } catch (error) {
            errorMessage.value = 'An unexpected error occurred while fetching data';
            console.error('API Error:', error);
            return null;
        }
    };

    // Core pagination methods
    const loadPage = async (page: number, resetData: boolean = false): Promise<void> => {
        // Check if page is already loaded (unless it's a reset)
        if (!resetData && loadedPagesData.has(page)) {
            switchToPage(page);
            return;
        }

        pagination.isLoading = true;

        try {
            const params = buildApiParams(page);
            params.page = page;
            params.limit = pagination.pageSize;

            const result = await getApiData(params);

            if (result) {
                // Store page data
                loadedPagesData.set(page, result.data);

                // Update loaded pages list for dropdown
                if (!pagination.loadedPages.includes(page)) {
                    pagination.loadedPages.push(page);
                    pagination.loadedPages.sort((a, b) => a - b); // Keep sorted
                }

                // Update pagination state
                pagination.currentPage = page;
                pagination.hasNext = result.pagination.hasNext;
                pagination.isInitialLoad = false;

                // Reset all data if needed (filter change or initial load)
                if (resetData) {
                    loadedPagesData.clear();
                    loadedPagesData.set(page, result.data);
                    pagination.loadedPages = [page];
                }

                // Set current page data
                currentPageData.value = result.data;
            }
        } finally {
            pagination.isLoading = false;
        }
    };

    const switchToPage = (page: number): void => {
        if (loadedPagesData.has(page)) {
            pagination.currentPage = page;
            currentPageData.value = loadedPagesData.get(page) || [];
        }
    };

    const resetPagination = (): void => {
        pagination.currentPage = 1;
        pagination.hasNext = false;
        pagination.loadedPages = [];
        pagination.isInitialLoad = true;
        loadedPagesData.clear();
        currentPageData.value = [];
    };

    // Navigation methods
    const nextPage = async (): Promise<void> => {
        if (pagination.hasNext && !pagination.isLoading) {
            await loadPage(pagination.currentPage + 1);
        }
    };

    const previousPage = (): void => {
        if (pagination.currentPage > 1) {
            const prevPage = pagination.currentPage - 1;
            // Previous page should already be loaded, so just switch to it
            switchToPage(prevPage);
        }
    };

    const selectPage = (selectedPage: number): void => {
        if (selectedPage) {
            // Page should already be loaded (dropdown only shows loaded pages)
            switchToPage(selectedPage);
        }
    };

    const changePageSize = async (): Promise<void> => {
        // Reset pagination when page size changes
        resetPagination();
        await loadPage(1, true);
    };

    // Utility method to update item in all data structures
    const updateItemInAllPages = (itemId: string | number, idField: string, propertyName: string, value: any): void => {
        // Update current page data
        const currentPageItem: any = currentPageData.value.find((item: any) => item[idField] === itemId);
        if (currentPageItem) {
            currentPageItem[propertyName] = value;
        }

        // Update stored page data
        const currentPage = pagination.currentPage;
        if (loadedPagesData.has(currentPage)) {
            const pageData = loadedPagesData.get(currentPage) || [];
            const storedItem: any = pageData.find((item: any) => item[idField] === itemId);
            if (storedItem) {
                storedItem[propertyName] = value;
                loadedPagesData.set(currentPage, pageData);
            }
        }
    };

    // Utility method to remove item from all data structures
    const removeItemFromAllPages = (itemId: string | number, idField: string): void => {
        // Remove from current page data
        currentPageData.value = currentPageData.value.filter((item: any) => item[idField] !== itemId);

        // Update the stored page data
        const currentPage = pagination.currentPage;
        if (loadedPagesData.has(currentPage)) {
            const pageData = loadedPagesData.get(currentPage) || [];
            loadedPagesData.set(
                currentPage,
                pageData.filter((item: any) => item[idField] !== itemId)
            );
        }
    };

    // Initialize - load first page
    const initialize = async (): Promise<void> => {
        await loadPage(1, true);
    };

    return {
        // State
        pagination,
        currentPageData,
        errorMessage,

        // Methods
        loadPage,
        switchToPage,
        resetPagination,
        nextPage,
        previousPage,
        selectPage,
        changePageSize,
        updateItemInAllPages,
        removeItemFromAllPages,
        initialize
    };
}
